import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowRight, CheckCircle2, FileSpreadsheet, Loader2, AlertTriangle } from "lucide-react";

const ENTITIES = [
  { key: "dealers", label: "Dealers" },
  { key: "products", label: "Products" },
  { key: "painters", label: "Painters" },
];
const STEPS = ["Upload", "Map Columns", "Preview", "Validate", "Result"];

export default function Imports() {
  const [entity, setEntity] = useState("dealers");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/imports/history").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("entity", entity);
    try {
      const { data } = await api.post("/imports/preview", fd);
      setPreview(data);
      const auto = {};
      data.target_fields.forEach((tf) => {
        const m = data.columns.find((c) => c.toLowerCase().replace(/[^a-z]/g, "").includes(tf.replace(/[^a-z]/g, "").slice(0, 4)));
        if (m) auto[tf] = m;
      });
      setMapping(auto); setStep(1);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doExecute = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("entity", entity); fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/imports/execute", fd);
      setResult(data); setStep(4); loadHistory();
      toast.success(`Imported ${data.valid} records`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setResult(null); setStep(0); };

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Data Import Wizard</h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">Upload → map → preview → validate → import. Normalization, dedup & exception queues built in.</p>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto scrollbar-thin">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${i === step ? "bg-[#8B0000] text-white" : i < step ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
              <span className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>{s}
            </div>
            {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        {step === 0 && (
          <div>
            <label className="text-sm font-semibold text-gray-700">Entity type</label>
            <div className="mt-2 flex gap-2 mb-5">
              {ENTITIES.map((e) => (
                <button key={e.key} data-testid={`import-entity-${e.key}`} onClick={() => setEntity(e.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 ${entity === e.key ? "border-[#8B0000] bg-[#FDF2F2] text-[#8B0000]" : "border-gray-200 text-gray-600"}`}>{e.label}</button>
              ))}
            </div>
            <label className="border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-[#8B0000] transition-colors" data-testid="import-dropzone">
              <FileSpreadsheet className="h-10 w-10 text-gray-400" />
              <span className="mt-3 font-semibold text-gray-700">{file ? file.name : "Choose an Excel / CSV file"}</span>
              <span className="text-xs text-gray-400 mt-1">.xlsx, .xls, .csv</span>
              <input data-testid="import-wizard-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </label>
            <button data-testid="import-preview-btn" disabled={!file || busy} onClick={doPreview}
              className="mt-5 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50 flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Detect Columns
            </button>
          </div>
        )}

        {step === 1 && preview && (
          <div>
            <p className="text-sm text-gray-500 mb-3">Detected header row <b>#{preview.detected_header_row + 1}</b> · {preview.row_count} data rows. Map each target field:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {preview.target_fields.map((tf) => (
                <div key={tf} className="flex items-center gap-2">
                  <span className="w-32 text-sm font-semibold text-gray-700 capitalize">{tf.replace(/_/g, " ")}</span>
                  <select data-testid={`map-${tf}`} value={mapping[tf] || ""} onChange={(e) => setMapping({ ...mapping, [tf]: e.target.value })}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]">
                    <option value="">— not mapped —</option>
                    {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setStep(2)} data-testid="import-to-preview" className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg">Preview Data →</button>
              <button onClick={reset} className="border border-gray-300 px-5 py-2.5 rounded-lg text-sm font-semibold">Cancel</button>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div>
            <p className="text-sm text-gray-500 mb-3">Sample of mapped data (first rows):</p>
            <div className="overflow-x-auto scrollbar-thin border rounded-lg">
              <table className="w-full text-xs text-left">
                <thead><tr className="bg-gray-50 border-b">{preview.target_fields.map((tf) => <th key={tf} className="py-2 px-3 font-bold uppercase tracking-wider text-gray-600 capitalize">{tf.replace(/_/g, " ")}</th>)}</tr></thead>
                <tbody>{preview.sample.slice(0, 6).map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">{preview.target_fields.map((tf) => <td key={tf} className="py-2 px-3 text-gray-700">{mapping[tf] ? String(row[mapping[tf]] ?? "") : "—"}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setStep(3)} data-testid="import-to-validate" className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg">Validate →</button>
              <button onClick={() => setStep(1)} className="border border-gray-300 px-5 py-2.5 rounded-lg text-sm font-semibold">Back</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 flex gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span>Duplicates (by unique key) are skipped, invalid rows rejected, and exceptions queued for review. Historical points import as opening balances only — no fake transactions.</span>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={doExecute} disabled={busy} data-testid="import-execute-btn" className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50 flex items-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Run Import
              </button>
              <button onClick={() => setStep(2)} className="border border-gray-300 px-5 py-2.5 rounded-lg text-sm font-semibold">Back</button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="text-center py-4" data-testid="import-result">
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
            <h3 className="mt-3 font-display text-xl font-bold text-gray-900">Import Complete</h3>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {[["Imported", result.valid, "green"], ["Duplicates", result.duplicates_skipped, "amber"], ["Rejected", result.rejected, "red"], ["Exceptions", result.exceptions, "blue"]].map(([l, v, t]) => (
                <div key={l} className={`p-4 rounded-xl bg-${t}-50 border border-${t}-200`}>
                  <div className="kpi-num text-2xl text-gray-900">{v}</div><div className="text-xs text-gray-500">{l}</div>
                </div>
              ))}
            </div>
            <button onClick={reset} className="mt-6 border-2 border-[#8B0000] text-[#8B0000] font-bold px-6 py-2.5 rounded-lg">Import Another File</button>
          </div>
        )}
      </div>

      <h3 className="font-display font-bold text-gray-900 mt-8 mb-3">Import History</h3>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-gray-50 border-b">{["Entity", "File", "Imported", "Duplicates", "Rejected", "Exceptions", "By", "When"].map((h) => <th key={h} className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">{h}</th>)}</tr></thead>
          <tbody>{history.map((h) => (
            <tr key={h.id} className="border-b border-gray-100" data-testid={`import-history-${h.id}`}>
              <td className="py-2.5 px-4 capitalize font-semibold">{h.entity}</td>
              <td className="py-2.5 px-4 text-gray-500 text-xs">{h.filename}</td>
              <td className="py-2.5 px-4 text-green-600 font-bold">{h.valid}</td>
              <td className="py-2.5 px-4 text-amber-600">{h.duplicates_skipped}</td>
              <td className="py-2.5 px-4 text-red-600">{h.rejected}</td>
              <td className="py-2.5 px-4 text-blue-600">{h.exceptions}</td>
              <td className="py-2.5 px-4 text-gray-500 text-xs">{h.by}</td>
              <td className="py-2.5 px-4 text-gray-400 text-xs">{new Date(h.created_at).toLocaleString("en-IN")}</td>
            </tr>))}
            {history.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-gray-400">No imports yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
